import { atom, onSet, task } from 'nanostores'

import listStates from '@/services/listStates'
import type { State } from '@/services/schemas'
import { $countries } from '@/stores/Countries'
import { $country } from '@/stores/Country'

const $states = atom<State[]>([])
const $isLoadingStates = atom<boolean>(false)

// Check if the selected country has state selection
const hasStateSelection = () => {
  const countryCode = $country.get()
  const countries = $countries.get()
  const country = countries.find(c => c.isocode2 === countryCode)
  return country?.has_state_selection === '1'
}

// Fetch states for a given country code
const fetchStatesForCountry = async (countryCode: string) => {
  const countries = $countries.get()
  const country = countries.find(c => c.isocode2 === countryCode)

  // If countries aren't loaded yet, skip (will be handled by onSet($countries))
  if (countries.length === 0) {
    return
  }

  if (country?.has_state_selection !== '1') {
    $states.set([])
    return
  }

  $isLoadingStates.set(true)

  try {
    const statesData = await listStates(countryCode)
    $states.set(statesData)
  }
  catch (error) {
    console.error('Failed to fetch states:', error)
    $states.set([])
  }
  finally {
    $isLoadingStates.set(false)
  }
}

// Fetch states when country changes
onSet($country, ({ newValue }) => {
  if (!newValue) {
    $states.set([])
    return
  }

  task(() => fetchStatesForCountry(newValue))
})

// Also fetch states when countries are loaded (handles preselection case)
onSet($countries, ({ newValue }) => {
  const countryCode = $country.get()
  if (!countryCode || newValue.length === 0) return

  task(() => fetchStatesForCountry(countryCode))
})

export {
  $isLoadingStates,
  $states,
  hasStateSelection,
}
